import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Shipment Tracker Tab — Search shipments, view details, map origin→destination
 * APIs: #27 (View Options), #28 (Shipment Details), #29 (AES ITN)
 * Features: Origin/destination map with custom pins using Leaflet
 */
import { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import { ifsService } from '../../../services/ifsService';
import styled, { keyframes } from 'styled-components';
import { ToolSection, SectionTitle, FormGrid, FormField, FieldLabel, FieldInput, FieldSelect, ActionButton, ResultPanel, ResultHeader, ResultTitle, ResponseTime, StatusBadge, InfoRow, InfoLabel, InfoValue, DataTable, TableWrapper, DetailGrid, DetailColumn, DetailTitle, SearchBar, EmptyState, SubSection, SubSectionHeader, } from './SharedStyles';
import HelpTip from './HelpTip';
// Import leaflet CSS
import 'leaflet/dist/leaflet.css';
// ─── Map Styles ──────────────────────────────────────────
const MapContainer = styled.div `
  width: 100%;
  height: 420px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  position: relative;
  margin-bottom: 24px;
  overflow: hidden;

  .leaflet-container {
    height: 100%;
    width: 100%;
    background: #1a1a2e;
  }

  /* Dark tile styling */
  .leaflet-tile-pane {
    filter: invert(1) hue-rotate(180deg) brightness(0.85) contrast(1.2) saturate(0.3);
  }

  .leaflet-control-zoom a {
    background: var(--bg-secondary) !important;
    color: var(--text-primary) !important;
    border-color: var(--border-color) !important;
  }

  .leaflet-control-attribution {
    background: rgba(0,0,0,0.6) !important;
    color: var(--text-tertiary) !important;
    font-size: 9px !important;

    a {
      color: var(--text-tertiary) !important;
    }
  }
`;
const MapLegend = styled.div `
  position: absolute;
  bottom: 12px;
  left: 12px;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.8);
  border: 1px solid var(--border-color);
  padding: 10px 14px;
  font-size: 11px;
  color: var(--text-secondary);
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const LegendItem = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '';
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${p => p.$color};
    box-shadow: 0 0 8px ${p => p.$color}80;
    flex-shrink: 0;
  }
`;
const pulse = keyframes `
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.2); opacity: 0.7; }
`;
const MapOverlay = styled.div `
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.85);
  border: 1px solid var(--accent-gold, #B8860B);
  padding: 12px 16px;
  color: var(--text-primary);
  font-size: 12px;
  max-width: 250px;
`;
const OverlayTitle = styled.div `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--accent-gold, #B8860B);
  margin-bottom: 8px;
`;
const DocumentLink = styled.a `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  color: var(--accent-gold, #B8860B);
  text-decoration: none;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s;
  margin-right: 8px;
  margin-bottom: 8px;

  &:hover {
    border-color: var(--accent-gold, #B8860B);
    background: var(--accent-gold-bg, rgba(184, 134, 11, 0.08));
  }
`;
const DocumentLinks = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;
// Custom marker SVG icons
const createPinIcon = (color, label) => {
    const svg = `
    <svg width="36" height="48" viewBox="0 0 36 48" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="${color}" flood-opacity="0.4"/>
        </filter>
      </defs>
      <path d="M18 47C18 47 33 30 33 18C33 9.716 26.284 3 18 3C9.716 3 3 9.716 3 18C3 30 18 47 18 47Z"
            fill="${color}" stroke="#000" stroke-width="1.5" filter="url(#shadow)"/>
      <circle cx="18" cy="18" r="8" fill="#000" opacity="0.3"/>
      <circle cx="18" cy="18" r="6" fill="#fff"/>
      <text x="18" y="21" text-anchor="middle" font-size="9" font-weight="700" fill="${color}"
            font-family="'Playfair Display', serif">${label}</text>
    </svg>
  `;
    return L.divIcon({
        html: svg,
        className: '',
        iconSize: [36, 48],
        iconAnchor: [18, 48],
        popupAnchor: [0, -48],
    });
};
// Simple geocoding based on US zip codes (approximate center coordinates)
// In production, you'd use a geocoding API, but this gives us reasonable positions
const US_STATE_COORDS = {
    'AL': [32.8, -86.8], 'AK': [64.2, -152.5], 'AZ': [34.0, -111.1], 'AR': [34.8, -92.2],
    'CA': [36.8, -119.4], 'CO': [39.0, -105.8], 'CT': [41.6, -72.7], 'DE': [39.0, -75.5],
    'FL': [27.7, -81.7], 'GA': [32.2, -83.4], 'HI': [19.9, -155.6], 'ID': [44.1, -114.7],
    'IL': [40.6, -89.4], 'IN': [40.3, -86.1], 'IA': [42.0, -93.2], 'KS': [38.5, -96.7],
    'KY': [37.8, -84.3], 'LA': [30.9, -92.3], 'ME': [45.3, -69.4], 'MD': [39.0, -76.6],
    'MA': [42.4, -71.4], 'MI': [44.3, -85.6], 'MN': [46.4, -94.6], 'MS': [32.4, -89.4],
    'MO': [37.9, -91.8], 'MT': [46.8, -110.4], 'NE': [41.5, -99.9], 'NV': [38.8, -116.4],
    'NH': [43.2, -71.6], 'NJ': [40.1, -74.5], 'NM': [34.5, -106.0], 'NY': [42.2, -74.9],
    'NC': [35.6, -79.8], 'ND': [47.5, -100.5], 'OH': [40.4, -82.9], 'OK': [35.5, -97.4],
    'OR': [43.8, -120.6], 'PA': [41.2, -77.2], 'RI': [41.7, -71.5], 'SC': [33.8, -81.2],
    'SD': [43.9, -99.4], 'TN': [35.5, -86.6], 'TX': [31.9, -99.9], 'UT': [39.3, -111.1],
    'VT': [44.0, -72.7], 'VA': [37.4, -78.7], 'WA': [47.8, -120.7], 'WV': [38.6, -80.9],
    'WI': [43.8, -88.8], 'WY': [43.1, -107.6], 'DC': [38.9, -77.0],
    // State names (lowercase)
    'alabama': [32.8, -86.8], 'alaska': [64.2, -152.5], 'arizona': [34.0, -111.1],
    'arkansas': [34.8, -92.2], 'california': [36.8, -119.4], 'colorado': [39.0, -105.8],
    'connecticut': [41.6, -72.7], 'delaware': [39.0, -75.5], 'florida': [27.7, -81.7],
    'georgia': [32.2, -83.4], 'hawaii': [19.9, -155.6], 'idaho': [44.1, -114.7],
    'illinois': [40.6, -89.4], 'indiana': [40.3, -86.1], 'iowa': [42.0, -93.2],
    'kansas': [38.5, -96.7], 'kentucky': [37.8, -84.3], 'louisiana': [30.9, -92.3],
    'maine': [45.3, -69.4], 'maryland': [39.0, -76.6], 'massachusetts': [42.4, -71.4],
    'michigan': [44.3, -85.6], 'minnesota': [46.4, -94.6], 'mississippi': [32.4, -89.4],
    'missouri': [37.9, -91.8], 'montana': [46.8, -110.4], 'nebraska': [41.5, -99.9],
    'nevada': [38.8, -116.4], 'new hampshire': [43.2, -71.6], 'new jersey': [40.1, -74.5],
    'new mexico': [34.5, -106.0], 'new york': [42.2, -74.9], 'north carolina': [35.6, -79.8],
    'north dakota': [47.5, -100.5], 'ohio': [40.4, -82.9], 'oklahoma': [35.5, -97.4],
    'oregon': [43.8, -120.6], 'pennsylvania': [41.2, -77.2], 'rhode island': [41.7, -71.5],
    'south carolina': [33.8, -81.2], 'south dakota': [43.9, -99.4], 'tennessee': [35.5, -86.6],
    'texas': [31.9, -99.9], 'utah': [39.3, -111.1], 'vermont': [44.0, -72.7],
    'virginia': [37.4, -78.7], 'washington': [47.8, -120.7], 'west virginia': [38.6, -80.9],
    'wisconsin': [43.8, -88.8], 'wyoming': [43.1, -107.6],
};
const getCoords = (state, city) => {
    if (!state)
        return null;
    const key = state.trim().toUpperCase();
    if (US_STATE_COORDS[key])
        return US_STATE_COORDS[key];
    const keyLower = state.trim().toLowerCase();
    if (US_STATE_COORDS[keyLower])
        return US_STATE_COORDS[keyLower];
    return [39.8, -98.6]; // US center fallback
};
const ShipmentTrackerTab = () => {
    const [searchType, setSearchType] = useState('tracking_no');
    const [searchValue, setSearchValue] = useState('');
    const [shipmentDetails, setShipmentDetails] = useState(null);
    const [labelLoading, setLabelLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [responseTime, setResponseTime] = useState(0);
    const [error, setError] = useState(null);
    // ITN check
    const [itnResult, setItnResult] = useState(null);
    const [itnLoading, setItnLoading] = useState(false);
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const handleSearch = async () => {
        if (!searchValue.trim())
            return;
        setLoading(true);
        setError(null);
        setShipmentDetails(null);
        try {
            const params = { [searchType]: searchValue.trim() };
            const detailsRes = await ifsService.getShipmentDetails(params);
            if (detailsRes.ok && detailsRes.data?.success) {
                setShipmentDetails(detailsRes.data.data);
                setResponseTime(detailsRes.data.response_time_ms);
            }
            else {
                setError(detailsRes.data?.data?.message || detailsRes.error || 'Shipment not found');
            }
        }
        catch (err) {
            setError(err.message);
        }
        finally {
            setLoading(false);
        }
    };
    // v2.3: IFS removed the document-links endpoint; the label is fetched as a PDF on demand
    const handleViewLabel = async () => {
        setLabelLoading(true);
        try {
            const params = { [searchType]: searchValue.trim() };
            const res = await ifsService.getLabelPDF(params);
            const b64 = res.data?.data?.pdf_base64;
            if (res.ok && res.data?.success && b64) {
                const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
                const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
                window.open(url, '_blank', 'noopener');
            }
            else {
                setError('Label PDF not available for this shipment');
            }
        }
        catch (err) {
            setError(err.message);
        }
        finally {
            setLabelLoading(false);
        }
    };
    const handleCheckITN = async (shipmentId) => {
        setItnLoading(true);
        const res = await ifsService.checkAESITN(shipmentId);
        if (res.ok && res.data?.success) {
            setItnResult(res.data.data);
        }
        setItnLoading(false);
    };
    // Map initialization and updates
    useEffect(() => {
        if (!shipmentDetails || !mapRef.current)
            return;
        const sender = shipmentDetails.sender_info;
        const recipient = shipmentDetails.recipient_info;
        const senderCoords = getCoords(sender?.customer_state, sender?.customer_city);
        const recipientCoords = getCoords(recipient?.client_state, recipient?.client_city);
        if (!senderCoords || !recipientCoords)
            return;
        // Destroy existing map
        if (mapInstanceRef.current) {
            mapInstanceRef.current.remove();
            mapInstanceRef.current = null;
        }
        const map = L.map(mapRef.current, {
            zoomControl: true,
            attributionControl: true,
        });
        mapInstanceRef.current = map;
        // Add dark-style tiles
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap',
            maxZoom: 18,
        }).addTo(map);
        // Create custom markers
        const originIcon = createPinIcon('#00c853', 'O');
        const destIcon = createPinIcon('#B8860B', 'D');
        const originMarker = L.marker(senderCoords, { icon: originIcon })
            .addTo(map)
            .bindPopup(`
        <div style="font-family: sans-serif; min-width: 200px;">
          <strong style="color: #00c853;">ORIGIN</strong><br/>
          <b>${sender.customer_company_name || sender.customer_name}</b><br/>
          ${sender.customer_address1}<br/>
          ${sender.customer_city}, ${sender.customer_state} ${sender.customer_zip}<br/>
          ${sender.customer_country}
        </div>
      `);
        const destMarker = L.marker(recipientCoords, { icon: destIcon })
            .addTo(map)
            .bindPopup(`
        <div style="font-family: sans-serif; min-width: 200px;">
          <strong style="color: #B8860B;">DESTINATION</strong><br/>
          <b>${recipient.client_name}</b><br/>
          ${recipient.client_address1}<br/>
          ${recipient.client_city}, ${recipient.client_state} ${recipient.client_zip}<br/>
          ${recipient.client_country}
        </div>
      `);
        // Draw route line with animation
        const routeLine = L.polyline([senderCoords, recipientCoords], {
            color: '#B8860B',
            weight: 3,
            opacity: 0.8,
            dashArray: '10, 8',
        }).addTo(map);
        // Add a glow effect line behind
        L.polyline([senderCoords, recipientCoords], {
            color: '#B8860B',
            weight: 8,
            opacity: 0.15,
        }).addTo(map);
        // Fit bounds with padding
        const bounds = L.latLngBounds([senderCoords, recipientCoords]);
        map.fitBounds(bounds, { padding: [60, 60] });
        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, [shipmentDetails]);
    return (_jsxs(_Fragment, { children: [_jsxs(ToolSection, { children: [_jsxs(SectionTitle, { children: ["Track ", _jsx("span", { children: "Shipment" }), _jsx(HelpTip, { title: "What is this?", text: "Look up any past or active shipment by its FedEx tracking number or IFS shipment ID. Shows full details including sender/recipient, costs, delivery status, documents, and an origin-to-destination map.", example: "Enter tracking number 7489234567890 to see where a client's diamond shipment is and download the shipping label." })] }), _jsxs(SearchBar, { children: [_jsxs(FormField, { style: { minWidth: 160 }, children: [_jsx(FieldLabel, { children: "Search By" }), _jsxs(FieldSelect, { value: searchType, onChange: (e) => setSearchType(e.target.value), children: [_jsx("option", { value: "tracking_no", children: "Tracking Number" }), _jsx("option", { value: "shipment_id", children: "Shipment ID" })] })] }), _jsxs(FormField, { style: { flex: 1 }, children: [_jsx(FieldLabel, { children: searchType === 'tracking_no' ? 'Tracking Number' : 'Shipment ID' }), _jsx(FieldInput, { placeholder: searchType === 'tracking_no' ? 'Enter FedEx tracking number...' : 'Enter IFS shipment ID...', value: searchValue, onChange: e => setSearchValue(e.target.value), onKeyDown: e => e.key === 'Enter' && handleSearch() })] }), _jsx(ActionButton, { onClick: handleSearch, disabled: loading, style: { alignSelf: 'flex-end' }, children: loading ? 'Searching...' : 'Track' })] }), error && (_jsx(ResultPanel, { "$status": "error", children: _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Error" }), _jsx(InfoValue, { children: error })] }) }))] }), shipmentDetails && (_jsxs(ToolSection, { children: [_jsxs(SectionTitle, { children: ["Shipment ", _jsx("span", { children: "Map" }), _jsx(HelpTip, { title: "What is this?", text: "Visual map showing the origin (sender) and destination (recipient) of this shipment. Green pin = origin, gold pin = destination. Click pins for full address details.", example: "Quickly see that a shipment is going from Pasadena, CA to New York, NY at a glance." })] }), _jsxs(MapContainer, { children: [_jsx("div", { ref: mapRef, style: { height: '100%', width: '100%' } }), _jsxs(MapLegend, { children: [_jsx(LegendItem, { "$color": "#00c853", children: "Origin (Sender)" }), _jsx(LegendItem, { "$color": "#B8860B", children: "Destination (Recipient)" })] }), _jsxs(MapOverlay, { children: [_jsx(OverlayTitle, { children: shipmentDetails.package_shipment_info?.tracking_no || 'Shipment' }), _jsxs("div", { style: { fontSize: 11, color: 'var(--text-secondary)' }, children: [shipmentDetails.package_shipment_info?.service_type, " \u2022", ' ', shipmentDetails.package_shipment_info?.fedex_status || 'Unknown Status'] }), shipmentDetails.delivery_information?.delivered_date && (_jsx("div", { style: { marginTop: 6, fontSize: 11 }, children: _jsxs(StatusBadge, { "$status": "success", children: ["Delivered ", shipmentDetails.delivery_information.delivered_date] }) }))] })] })] })), shipmentDetails && (_jsxs(ToolSection, { children: [_jsxs(ResultHeader, { children: [_jsx(ResultTitle, { children: "Shipment Details" }), _jsxs(ResponseTime, { children: [responseTime, "ms"] })] }), _jsxs(DetailGrid, { children: [_jsxs(DetailColumn, { children: [_jsx(DetailTitle, { children: "Sender (Origin)" }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Name" }), _jsx(InfoValue, { children: shipmentDetails.sender_info?.customer_name })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Company" }), _jsx(InfoValue, { children: shipmentDetails.sender_info?.customer_company_name })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Address" }), _jsx(InfoValue, { children: shipmentDetails.sender_info?.customer_address1 })] }), shipmentDetails.sender_info?.customer_address2 && (_jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Address 2" }), _jsx(InfoValue, { children: shipmentDetails.sender_info.customer_address2 })] })), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "City/State/Zip" }), _jsxs(InfoValue, { children: [shipmentDetails.sender_info?.customer_city, ", ", shipmentDetails.sender_info?.customer_state, " ", shipmentDetails.sender_info?.customer_zip] })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Country" }), _jsx(InfoValue, { children: shipmentDetails.sender_info?.customer_country })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Phone" }), _jsx(InfoValue, { children: shipmentDetails.sender_info?.customer_phone })] })] }), _jsxs(DetailColumn, { children: [_jsx(DetailTitle, { children: "Recipient (Destination)" }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Name" }), _jsx(InfoValue, { children: shipmentDetails.recipient_info?.client_name })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Label Name" }), _jsx(InfoValue, { children: shipmentDetails.recipient_info?.client_label_name })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Address" }), _jsx(InfoValue, { children: shipmentDetails.recipient_info?.client_address1 })] }), shipmentDetails.recipient_info?.client_address2 && (_jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Address 2" }), _jsx(InfoValue, { children: shipmentDetails.recipient_info.client_address2 })] })), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "City/State/Zip" }), _jsxs(InfoValue, { children: [shipmentDetails.recipient_info?.client_city, ", ", shipmentDetails.recipient_info?.client_state, " ", shipmentDetails.recipient_info?.client_zip] })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Country" }), _jsx(InfoValue, { children: shipmentDetails.recipient_info?.client_country })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Phone" }), _jsx(InfoValue, { children: shipmentDetails.recipient_info?.client_phone })] })] })] }), _jsxs(SubSection, { style: { marginTop: 20 }, children: [_jsx(SubSectionHeader, { children: _jsx("h4", { children: "Package & Shipment Info" }) }), _jsx(FormGrid, { "$columns": 4, children: shipmentDetails.package_shipment_info && Object.entries({
                                    'Tracking #': shipmentDetails.package_shipment_info.tracking_no,
                                    'Confirmation #': shipmentDetails.package_shipment_info.confirmation_no,
                                    'Service': shipmentDetails.package_shipment_info.service_type,
                                    'Status': shipmentDetails.package_shipment_info.fedex_status,
                                    'Packaging': shipmentDetails.package_shipment_info.packaging_type,
                                    'Weight': `${shipmentDetails.package_shipment_info.package_weight} lbs`,
                                    'Declared Value': `$${shipmentDetails.package_shipment_info.declare_value}`,
                                    'Zone': shipmentDetails.package_shipment_info.delivery_zone,
                                    'Pickup Date': shipmentDetails.package_shipment_info.pickup_date,
                                    'Signature': shipmentDetails.package_shipment_info.signature,
                                    'Residential': shipmentDetails.package_shipment_info.residential === '1' ? 'Yes' : 'No',
                                    'Saturday Delivery': shipmentDetails.package_shipment_info.saturday_delivery === '1' ? 'Yes' : 'No',
                                }).map(([label, value]) => (_jsxs("div", { children: [_jsx(FieldLabel, { children: label }), _jsx("div", { style: { color: 'var(--text-primary)', fontSize: 13, marginTop: 4 }, children: value || '—' })] }, label))) })] }), shipmentDetails.package_shipment_info?.packaging_dim_length && (_jsxs(SubSection, { children: [_jsx(SubSectionHeader, { children: _jsx("h4", { children: "Dimensions" }) }), _jsxs("div", { style: { color: 'var(--text-primary)', fontSize: 14 }, children: [shipmentDetails.package_shipment_info.packaging_dim_length, "\" L \u00D7", ' ', shipmentDetails.package_shipment_info.packaging_dim_width, "\" W \u00D7", ' ', shipmentDetails.package_shipment_info.packaging_dim_height, "\" H"] })] })), shipmentDetails.cost_info && shipmentDetails.cost_info.length > 0 && (_jsxs(SubSection, { children: [_jsx(SubSectionHeader, { children: _jsx("h4", { children: "Cost Breakdown" }) }), _jsx(TableWrapper, { children: _jsxs(DataTable, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Item" }), _jsx("th", { children: "Amount" })] }) }), _jsx("tbody", { children: shipmentDetails.cost_info.map((cost, i) => (_jsxs("tr", { children: [_jsx("td", { children: cost.text }), _jsx("td", { style: { fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }, children: cost.value })] }, i))) })] }) })] })), shipmentDetails.delivery_information && (_jsxs(SubSection, { children: [_jsx(SubSectionHeader, { children: _jsx("h4", { children: "Delivery Information" }) }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Delivered To" }), _jsx(InfoValue, { children: shipmentDetails.delivery_information.delivered_to || '—' })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Delivery Date" }), _jsx(InfoValue, { children: shipmentDetails.delivery_information.delivered_date || '—' })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Signature" }), _jsx(InfoValue, { children: shipmentDetails.delivery_information.delivered_signature || '—' })] })] })), _jsxs(SubSection, { children: [_jsx(SubSectionHeader, { children: _jsx("h4", { children: "Documents & Labels" }) }), _jsx(DocumentLinks, { children: _jsx(DocumentLink, { as: "button", onClick: handleViewLabel, disabled: labelLoading, style: { cursor: labelLoading ? 'wait' : 'pointer' }, children: labelLoading ? 'Loading label…' : 'View Shipping Label (PDF)' }) })] }), shipmentDetails.package_shipment_info?.tracking_no && (_jsxs(SubSection, { children: [_jsx(SubSectionHeader, { children: _jsx("h4", { children: "Live Tracking" }) }), _jsx(DocumentLink, { href: `https://www.fedex.com/fedextrack/?trknbr=${shipmentDetails.package_shipment_info.tracking_no}`, target: "_blank", rel: "noopener noreferrer", children: "Track on FedEx.com" })] })), shipmentDetails.international_info?.is_international === '1' && (_jsxs(SubSection, { children: [_jsx(SubSectionHeader, { children: _jsx("h4", { children: "International / Customs" }) }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Loading Port" }), _jsx(InfoValue, { children: shipmentDetails.international_info.loading_port_code })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Duties Paid By" }), _jsx(InfoValue, { children: shipmentDetails.international_info.duties_taxes_paid_by })] }), shipmentDetails.international_info.products?.length > 0 && (_jsx(TableWrapper, { style: { marginTop: 12 }, children: _jsxs(DataTable, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Product" }), _jsx("th", { children: "Description" }), _jsx("th", { children: "HTS #" }), _jsx("th", { children: "Qty" }), _jsx("th", { children: "Weight" }), _jsx("th", { children: "Value" })] }) }), _jsx("tbody", { children: shipmentDetails.international_info.products.map((p, i) => (_jsxs("tr", { children: [_jsx("td", { children: p.product }), _jsx("td", { children: p.description }), _jsx("td", { children: p.hts_number }), _jsx("td", { children: p.qty }), _jsx("td", { children: p.gross_weight }), _jsxs("td", { children: ["$", p.goods_value] })] }, i))) })] }) })), shipmentDetails.international_info.duties_total_amount && (_jsxs(InfoRow, { style: { marginTop: 12 }, children: [_jsx(InfoLabel, { children: "Total Duties" }), _jsxs(InfoValue, { style: { fontWeight: 700, color: 'var(--accent-gold)' }, children: ["$", shipmentDetails.international_info.duties_total_amount] })] })), _jsx(ActionButton, { "$variant": "secondary", onClick: () => handleCheckITN(shipmentDetails.package_shipment_info?.confirmation_no || ''), disabled: itnLoading, style: { marginTop: 12 }, children: itnLoading ? 'Checking...' : 'Check AES ITN Status' }), itnResult && (_jsx(ResultPanel, { "$status": "neutral", style: { marginTop: 12 }, children: _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "ITN Status" }), _jsx(InfoValue, { children: itnResult.message || itnResult.status })] }) }))] }))] })), !shipmentDetails && !loading && !error && (_jsx(EmptyState, { children: "Enter a tracking number or shipment ID to view shipment details and map" }))] }));
};
export default ShipmentTrackerTab;
